import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import api from '../utils/api';
import WhiteLabelOrderBump from './WhiteLabelOrderBump';
import OnboardingScheduler from './OnboardingScheduler';


const SignupSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const addon = searchParams.get('addon'); // 'white_label' if returning from white-label checkout
  const addonCanceled = searchParams.get('addon_canceled'); // if user canceled white-label checkout
  
  // Flow steps: 'checking' -> 'success' -> 'white_label_offer' -> 'onboarding' -> 'complete'
  const [flowStep, setFlowStep] = useState('checking');
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'failed'
  const [pollCount, setPollCount] = useState(0);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [whiteLabelLoading, setWhiteLabelLoading] = useState(false);
  const [userInfo, setUserInfo] = useState({ email: '', name: '' });
  const maxPollAttempts = 10;

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    
    // If returning from white-label addon checkout
    if (addon === 'white_label') {
      // User just purchased white label, go to onboarding
      setStatus('success');
      setFlowStep('onboarding');
      return;
    }
    
    // If user canceled white-label checkout, continue to onboarding
    if (addonCanceled === 'white_label') {
      setStatus('success');
      setFlowStep('onboarding');
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId, addon, addonCanceled]);

  const pollPaymentStatus = async () => {
    if (pollCount >= maxPollAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await api.get(`/billing/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setBillingCycle(response.data.billing_cycle || 'monthly');
        
        // Get stored signup info
        const pendingSignup = localStorage.getItem('pending_signup');
        if (pendingSignup) {
          const { email, name } = JSON.parse(pendingSignup);
          setUserInfo({ email, name: name || '' });
          localStorage.removeItem('pending_signup');
        }
        
        // Store the session for white-label checkout
        localStorage.setItem('signup_session_id', sessionId);
        
        // Brief success display, then show white label offer
        setTimeout(() => {
          setFlowStep('white_label_offer');
        }, 2000);
        
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        // Still pending, poll again
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  const handleWhiteLabelAccept = async () => {
    setWhiteLabelLoading(true);
    try {
      // Need to login first to get a token for the checkout
      // Try to get the pending signup credentials
      const pendingCreds = localStorage.getItem('pending_signup_creds');
      let token = localStorage.getItem('access_token');
      
      if (!token && pendingCreds) {
        const { email, password } = JSON.parse(pendingCreds);
        // Quick login to get token
        const loginResponse = await api.post('/auth/login', { email, password });
        if (loginResponse.data.access_token) {
          token = loginResponse.data.access_token;
          localStorage.setItem('access_token', token);
        }
      }
      
      if (!token) {
        // Can't create checkout without auth, skip to onboarding
        console.warn('No auth token available for white-label checkout');
        setFlowStep('onboarding');
        return;
      }
      
      const response = await api.post('/billing/checkout/white-label', {
        origin_url: window.location.origin,
        source: 'signup'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating white-label checkout:', error);
      // On error, just continue to onboarding
      setFlowStep('onboarding');
    } finally {
      setWhiteLabelLoading(false);
    }
  };

  const handleWhiteLabelDecline = () => {
    setFlowStep('onboarding');
  };

  const handleOnboardingComplete = () => {
    setFlowStep('complete');
    // Redirect to login after brief delay
    setTimeout(() => {
      navigate('/');
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full">
        
        {/* Step: Checking Payment */}
        {flowStep === 'checking' && status === 'checking' && (
          <div className="text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/30">
              <Loader2 className="w-16 h-16 text-purple-400 mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-4">Processing Your Payment</h1>
              <p className="text-purple-200 mb-4">
                Please wait while we confirm your payment...
              </p>
              <div className="w-full bg-purple-500/20 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(pollCount / maxPollAttempts) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step: Payment Success (brief) */}
        {flowStep === 'checking' && status === 'success' && (
          <div className="text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-green-500/30">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Payment Successful!</h1>
              <p className="text-purple-200">
                Setting up your account...
              </p>
            </div>
          </div>
        )}

        {/* Step: White Label Order Bump */}
        {flowStep === 'white_label_offer' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full text-green-400 text-sm font-medium mb-4">
                <CheckCircle className="w-4 h-4" />
                <span>Payment Confirmed</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">One More Thing...</h1>
              <p className="text-purple-200">
                Take your brand to the next level
              </p>
            </div>
            
            <WhiteLabelOrderBump
              billingCycle={billingCycle}
              onAccept={handleWhiteLabelAccept}
              onDecline={handleWhiteLabelDecline}
              loading={whiteLabelLoading}
            />
          </div>
        )}

        {/* Step: Onboarding Scheduler */}
        {flowStep === 'onboarding' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full text-green-400 text-sm font-medium mb-4">
                <CheckCircle className="w-4 h-4" />
                <span>Account Ready</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Almost There!</h1>
              <p className="text-purple-200">
                Let&apos;s get you set up for success
              </p>
            </div>
            
            <OnboardingScheduler
              onScheduled={handleOnboardingComplete}
              userEmail={userInfo.email}
              userName={userInfo.name}
            />
          </div>
        )}

        {/* Step: Complete */}
        {flowStep === 'complete' && (
          <div className="text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-green-500/30">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Welcome to Athena!</h1>
              <p className="text-purple-200 mb-6">
                Your account is ready. Redirecting you to login...
              </p>
              <div className="bg-purple-500/20 rounded-lg p-4 mb-6">
                <p className="text-purple-100 text-sm">
                  🎉 Check your email for a welcome message with next steps.
                </p>
              </div>
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                <span>Go to Login Now</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step: Failed */}
        {status === 'failed' && flowStep === 'checking' && (
          <div className="text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Payment Issue</h1>
              <p className="text-purple-200 mb-6">
                We couldn&apos;t confirm your payment. This could be a temporary issue.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setStatus('checking');
                    setPollCount(0);
                    pollPaymentStatus();
                  }}
                  className="w-full py-3 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 transition-colors"
                >
                  Check Again
                </button>
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full py-3 border border-purple-500/50 text-purple-200 font-medium rounded-lg hover:bg-purple-500/20 transition-colors"
                >
                  Return to Signup
                </button>
              </div>
              <p className="text-purple-300/70 text-sm mt-4">
                If your payment was processed, please contact support and we&apos;ll help you access your account.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupSuccess;
